<template>
    <PageContent type="center" :compClass="'flex flex-col space-y-6'">
        <div class="flex flex-row items-center justify-between">
            <h1 class="text-xl font-semibold">YouTube Analytics Report</h1>
        </div>
        
        <div class="flex flex-row justify-between">
            <div class="text-xl font-medium text-black">{{ report.start_date | displayMonth }}</div>
            <span class="text-right"><i class="fa-solid fa-clock mr-1 text-gray-400"></i> {{ report.created_at }}</span>
        </div>

        <div v-if="(report.statistics == null)">
          This report is currently running.
        </div>

        <div class="bg-red-600 text-white rounded-lg p-4 flex flex-row items-center justify-between" v-if="(report.status == 'pending')" >
          <div class="flex flex-col">
            <strong class="font-medium">Action Required</strong> You must approve this report before individual reports will be released to users.
          </div>
          <button class="bg-white text-black rounded-md px-3 py-2 grow-0 focus:outline-none hover:bg-gray-200" v-on:click="approveReport()">Approve</button>
        </div>

        <div class="grid grid-cols-2 gap-4 mt-4" v-if="(report.statistics !== null)">
            <div
                class="col-span-2 md:col-span-1 bg-white rounded-lg overflow-hidden shadow-md"
            >
                <div class="flex flex-row items-center justify-between p-4">
                <h1 class="text-lg">Stringer Earnings</h1>
                </div>

                <div class="px-4 pb-4 focus-within:font-medium text-4xl">
                {{ report.stringer_earnings | displayMoney }}
                </div>
            </div>
            <div
                class="col-span-2 md:col-span-1 bg-white rounded-lg overflow-hidden shadow-md"
            >
                <div class="flex flex-row items-center justify-between p-4">
                <h1 class="text-lg">Total Earnings</h1>
                </div>

                <div class="px-4 pb-4 focus-within:font-medium text-4xl">
                {{ report.total_earnings | displayMoney }}
                </div>
            </div>
        </div>

        <div class="w-full flex flex-col bg-white rounded-md shadow-md mt-4 p-4" v-if="(report.statistics !== null)">
            <div class="text-lg">Statistics</div>
            <div class="grid grid-cols-3 gap-4 mt-4">
                <div
                class="col-span-3 md:col-span-1 flex flex-row items-center content-center space-x-3"
                >
                <div
                    class="w-12 h-12 bg-blue-600 rounded-md flex items-center content-center justify-center text-white"
                >
                    <i class="fa fa-eye text-lg"></i>
                </div>
                <div class="flex flex-col">
                    <span class="text-2xl font-medium">{{
                    report.statistics.views.toLocaleString()
                    }}</span>
                    <span class="uppercase text-xs text-gray-500">Views</span>
                </div>
                </div>

                <div
                class="col-span-3 md:col-span-1 flex flex-row items-center content-center space-x-3"
                >
                <div
                    class="w-12 h-12 bg-blue-600 rounded-md flex items-center content-center justify-center text-white"
                >
                    <i class="fa fa-clock text-lg"></i>
                </div>
                <div class="flex flex-col">
                    <span class="text-2xl font-medium">{{
                    report.statistics.estimated_minutes_watched.toLocaleString()
                    }}</span>
                    <span class="uppercase text-xs text-gray-500">Minutes Watched</span>
                </div>
                </div>

                <div
                class="col-span-3 md:col-span-1 flex flex-row items-center content-center space-x-3"
                >
                <div
                    class="w-12 h-12 bg-blue-600 rounded-md flex items-center content-center justify-center text-white"
                >
                    <i class="fa fa-stopwatch text-lg"></i>
                </div>
                <div class="flex flex-col">
                    <span class="text-2xl font-medium">{{
                    report.statistics.avg_view_duration.toLocaleString()
                    }}</span>
                    <span class="uppercase text-xs text-gray-500"
                    >Average View Duration (seconds)</span
                    >
                </div>
                </div>
            </div>

            <div class="grid grid-cols-3 gap-4 mt-4 md:mt-6">
                <div
                class="col-span-3 md:col-span-1 flex flex-row items-center content-center space-x-3"
                >
                <div
                    class="w-12 h-12 bg-blue-600 rounded-md flex items-center content-center justify-center text-white"
                >
                    <i class="fa fa-sync text-lg"></i>
                </div>
                <div class="flex flex-col">
                    <span class="text-2xl font-medium">{{
                    report.statistics.ad_impressions.toLocaleString()
                    }}</span>
                    <span class="uppercase text-xs text-gray-500">Ad Impressions</span>
                </div>
                </div>

                <div
                class="col-span-3 md:col-span-1 flex flex-row items-center content-center space-x-3"
                >
                <div
                    class="w-12 h-12 bg-blue-600 rounded-md flex items-center content-center justify-center text-white"
                >
                    <i class="fa fa-thumbs-up text-lg"></i>
                </div>
                <div class="flex flex-col">
                    <span class="text-2xl font-medium">{{
                    report.statistics.likes.toLocaleString()
                    }}</span>
                    <span class="uppercase text-xs text-gray-500">Likes</span>
                </div>
                </div>

                <div
                class=" col-span-3 md:col-span-1 flex flex-row items-center content-center space-x-3"
                >
                <div
                    class="w-12 h-12 bg-blue-600 rounded-md flex items-center content-center justify-center text-white"
                >
                    <i class="fa fa-ad text-lg"></i>
                </div>
                <div class="flex flex-col">
                    <span class="text-2xl font-medium">{{
                    report.statistics.monetized_playbacks.toLocaleString()
                    }}</span>
                    <span class="uppercase text-xs text-gray-500"
                    >Monetized Playbacks</span
                    >
                </div>
                </div>
            </div>
        </div>

        <div
        class="-mx-4 mt-8 overflow-hidden shadow-md ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg"
        v-if="(report.statistics !== null)"
      >
        <table class="min-w-full divide-y divide-gray-300">
          <thead class="bg-gray-50">
            <tr class="hidden lg:table-row">
              <th></th>
              <th
                scope="col"
                class="py-3.5 px-3 text-left text-sm font-semibold text-gray-900"
              >
                User
              </th>
              <th
                scope="col"
                class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell"
              >
                Views
              </th>
              <th
                scope="col"
                class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell"
              >
                Stringer Earnings
              </th>
              <th
                scope="col"
                class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell"
              >
                Total Earnings
              </th>
              <th
                scope="col"
                class="hidden relative py-3.5 pl-3 pr-4 sm:pr-6 lg:table-cell"
              >
                <span class="sr-only">View Report</span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr v-for="userReport in report.user_video_reports" v-bind:key="userReport.user.id">
              <td class="py-3.5 px-3 text-center text-sm text-gray-800 w-8">
                  <i class="fa-duotone fa-money-bill-transfer" :class="{'text-yellow-600': userReport.status !== 'paid', 'text-green-600': userReport.status == 'paid' }" :title="userReport.status == 'paid' ? 'Paid' : 'Awaiting Payment'" v-tippy></i>
              </td>
              <td scope="col" class="py-3.5 px-3 text-left text-sm text-gray-800">
                {{ userReport.user.name }}
                <dl class="font-normal lg:hidden">
                  <dt class="mt-1 truncate text-gray-700">
                    {{ userReport.statistics.views.toLocaleString() }} views
                  </dt>
                  <dd class="mt-1 truncate text-gray-700">
                    ${{ userReport.stringer_earnings.toLocaleString() }}
                  </dd>
                </dl>
              </td>
              <td
                scope="col"
                class="hidden px-3 py-3.5 text-left text-sm text-gray-500 lg:table-cell"
              >
                {{ userReport.statistics.views.toLocaleString() }}
              </td>
              <td
                scope="col"
                class="hidden px-3 py-3.5 text-left text-sm text-gray-500 lg:table-cell"
              >
                {{ userReport.stringer_earnings | displayMoney }}
              </td>
              <td
                scope="col"
                class="hidden px-3 py-3.5 text-left text-sm text-gray-500 lg:table-cell"
              >
                {{ userReport.total_earnings | displayMoney }}
              </td>
              <td class="hidden relative py-3.5 px-3 lg:table-cell text-sm">
                <a href="#" class="text-blue-600 hover:text-blue-900"
                  >Statistics</a
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
    </PageContent>
</template>

<script>
import * as Config from "@/config";
import moment from "moment";
import PageContent from "@/components/PageContent";
import StatCard from "@/components/StatCard";
import { report } from "process";


export default {
  name: "YouTubeStatisticsReport",
  data() {
    return {
        report: {},
    };
  },
  components: {
    PageContent,
    StatCard,
  },
  mounted() {
    this.getAnalyticsReports();
  },
  methods: {
    async getAnalyticsReports() {
      let res = await this.$http.get(
        Config.apiPath + "/api/v2/admin/youtube/reports/" + this.$route.params.granularity_id,
      );
      this.report = res.data
    },
    async approveReport() {
      let res = await this.$http.post(
        Config.apiPath + "/api/v2/admin/youtube/reports/" + this.$route.params.granularity_id + "/approve"
      );
      await this.getAnalyticsReports()
      
    }
  },
  filters: {
    displayMonth: function(datetime) {
        return moment.utc(datetime).format('MMMM Y')
    },
    displayMoney: function(value) {
      var formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
      });
      return formatter.format(value);
    },
  }
};
</script>
